import { Theme } from '@mui/material/styles';

/**
 * Sizes for stacked action buttons (dialog/card footers when buttons stack on small screens).
 * Use in sx: minWidth: { xs: StackedActionButton.MIN_WIDTH, sm: 'auto' }, minHeight: { xs: StackedActionButton.MIN_HEIGHT, sm: 'auto' }.
 * Do not duplicate these numbers in components — import from here.
 */
export const StackedActionButton = {
    /** Minimum width for stacked pairs on xs (touch-friendly, centered columns). */
    MIN_WIDTH: 250,
    /** Minimum height for stacked / dialog actions (touch target). */
    MIN_HEIGHT: 44,
    /**
     * Minimum width for a stacked action column on `sm` and up (e.g. dense toolbars).
     * Use with `width: max-content` so the column is at least this wide on larger screens.
     */
    MIN_WIDTH_STACKED_COLUMN_SM: 280,
    /**
     * Narrower stacked column on `sm+` (e.g. quote dialog Accept + Chat with visible labels).
     */
    MIN_WIDTH_STACKED_COLUMN_SM_NARROW: 200,
    /**
     * Taller touch targets for the chat modal “New support” action stack on narrow viewports (`xs`).
     */
    CHAT_SUPPORT_ACTION_MIN_HEIGHT_XS: 52,
} as const;

/**
 * Mobile web keyboard avoidance (useKeyboardAvoidance + `.modal-with-inputs`).
 * Android Chrome often shows a saved-credential strip above the IME that visualViewport metrics omit.
 */
export const MobileKeyboardAvoidance = {
    /** Extra bottom inset (px) on Android mobile web when the soft keyboard is open. */
    ANDROID_CREDENTIAL_STRIP_EXTRA_PX: 56,
    /**
     * Never apply a larger inset than layoutHeight minus this — prevents collapsed modals if
     * viewport metrics misreport (e.g. transient innerHeight / offsetTop glitches).
     */
    MIN_LAYOUT_HEIGHT_FOR_MODAL_PX: 200,
} as const;

/** `sx` helper: `minWidth` for stacked button columns on sm+ only. */
export const stackedActionButtonStackColumnMinWidthSmSx = {
    sm: StackedActionButton.MIN_WIDTH_STACKED_COLUMN_SM,
} as const;

/** `sx` helper: narrower `minWidth` on sm+ for labeled stacked pairs (e.g. Accept + Chat). */
export const stackedActionButtonStackColumnMinWidthSmNarrowSx = {
    sm: StackedActionButton.MIN_WIDTH_STACKED_COLUMN_SM_NARROW,
} as const;

/** Responsive minWidth for stacked action buttons. Use in sx: minWidth: stackedActionButtonMinWidthSx */
export const stackedActionButtonMinWidthSx = { xs: StackedActionButton.MIN_WIDTH, sm: 'auto' as const };

/**
 * Fixed width on xs so compact stacked pairs stay equal without using the default 250px min width.
 * Same px value as {@link StackedActionButton.MIN_WIDTH_STACKED_COLUMN_SM_NARROW}.
 */
export const stackedActionButtonFixedWidthXsNarrowSx = {
    width: { xs: StackedActionButton.MIN_WIDTH_STACKED_COLUMN_SM_NARROW, sm: 'auto' as const },
    minWidth: { xs: StackedActionButton.MIN_WIDTH_STACKED_COLUMN_SM_NARROW, sm: 'auto' as const },
} as const;

/** Responsive minHeight for stacked action buttons. Use in sx: minHeight: stackedActionButtonMinHeightSx */
export const stackedActionButtonMinHeightSx = { xs: StackedActionButton.MIN_HEIGHT, sm: 'auto' as const };

/** Sx for action container (e.g. actionsSx) so all buttons get consistent min width/height when stacked. */
export const stackedActionButtonRootSx = {
    minWidth: stackedActionButtonMinWidthSx,
    minHeight: stackedActionButtonMinHeightSx,
} as const;

/** Min height for chat support “New support” buttons: taller on `xs`, default touch height from `sm`. */
export const chatSupportPanelStackedButtonMinHeightSx = {
    xs: StackedActionButton.CHAT_SUPPORT_ACTION_MIN_HEIGHT_XS,
    sm: StackedActionButton.MIN_HEIGHT,
} as const;

// ---------------------------------------------------------------------------
// Responsive font sizes (use in sx: fontSize: responsiveFontSize.body, etc.)
// ---------------------------------------------------------------------------

/** Responsive fontSize values for use in sx. Use these instead of hardcoded rem/px. */
export const responsiveFontSize = {
    /** Body text: 0.875rem → 1rem (mobile → sm+) */
    body: { xs: '0.875rem', sm: '1rem' } as const,
    /** Body fixed at 0.875rem on all breakpoints */
    bodyFixed: { xs: '0.875rem', sm: '0.875rem' } as const,
    /** Caption/small: 0.75rem → 0.875rem */
    caption: { xs: '0.75rem', sm: '0.875rem' } as const,
    /** Caption fixed at 0.75rem */
    captionFixed: { xs: '0.75rem', sm: '0.75rem' } as const,
    /** Subtext: 0.8125rem → 0.875rem */
    subtext: { xs: '0.8125rem', sm: '0.875rem' } as const,
    /** Subtext fixed at 0.8125rem on all breakpoints */
    subtextFixed: { xs: '0.8125rem', sm: '0.8125rem' } as const,
    /** Overline/tiny: 0.7rem → 0.75rem */
    overline: { xs: '0.7rem', sm: '0.75rem' } as const,
    /** Small body: 0.8rem → 0.875rem */
    bodySmall: { xs: '0.8rem', sm: '0.875rem' } as const,
    /** Subtitle / h6 style: 1rem → 1.25rem */
    subtitle: { xs: '1rem', sm: '1.25rem' } as const,
    /** Title / card heading: 1.05rem → 1.25rem */
    title: { xs: '1.05rem', sm: '1.25rem' } as const,
    /** Heading small: 1.1rem → 1.25rem */
    headingSm: { xs: '1.1rem', sm: '1.25rem' } as const,
    /** Heading medium: 1.25rem → 1.5rem */
    headingMd: { xs: '1.25rem', sm: '1.5rem' } as const,
    /** Heading medium with md: 1.1rem → 1.5rem (md) */
    headingMdBreakpoints: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } as const,
    /** Body medium: 0.9375rem → 1rem */
    bodyMedium: { xs: '0.9375rem', sm: '1rem' } as const,
    /** Body large: 1rem → 1.125rem */
    bodyLarge: { xs: '1rem', sm: '1.125rem' } as const,
    /** Body small to large (e.g. input helper): 0.875rem → 1.125rem */
    bodySmallToLarge: { xs: '0.875rem', sm: '1.125rem' } as const,
    /** Body compact: 0.9rem → 1rem */
    bodyCompact: { xs: '0.9rem', sm: '1rem' } as const,
    /** Body compact alt: 0.95rem → 1rem */
    bodyCompactAlt: { xs: '0.95rem', sm: '1rem' } as const,
    /** Body lead: 1rem → 1.1rem */
    bodyLead: { xs: '1rem', sm: '1.1rem' } as const,
    /** Caption alt: 0.85rem → 0.875rem */
    captionAlt: { xs: '0.85rem', sm: '0.875rem' } as const,
    /** Form label: 0.875rem → 0.95rem */
    label: { xs: '0.875rem', sm: '0.95rem' } as const,
    /** Step/subsection: 1.125rem → 1.25rem */
    h5: { xs: '1.125rem', sm: '1.25rem' } as const,
    /** Step label: 0.75rem → 0.8125rem */
    stepLabel: { xs: '0.75rem', sm: '0.8125rem' } as const,
    /** H3: 1.25rem → 1.5rem → 1.75rem */
    h3: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } as const,
    /** H2: 1.75rem → 2.25rem → 2.75rem */
    h2: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' } as const,
    /** H4 / hero: 1.5rem → 2rem */
    h4: { xs: '1.5rem', sm: '2rem' } as const,
    /** Funnel/small title: 0.9rem → 1.1rem (md) */
    funnelTitle: { xs: '0.9rem', md: '1.1rem' } as const,
    /** Order details title: 1rem → 1.0625rem */
    orderDetailsTitle: { xs: '1rem', sm: '1.0625rem' } as const,
    /** Body 0.875 → 0.9 (slightly smaller on sm) */
    bodyTight: { xs: '0.875rem', sm: '0.9rem' } as const,
    /** 0.875 → 0.9375 */
    bodyMenu: { xs: '0.875rem', sm: '0.9375rem' } as const,
    /** Availability label: 0.85rem → 0.9rem */
    availabilityLabel: { xs: '0.85rem', sm: '0.9rem' } as const,
    /** Hero/intro body: 0.95rem → 1.1rem */
    bodyHero: { xs: '0.95rem', sm: '1.1rem' } as const,
    /** Overline three breakpoints: 0.7rem → 0.75rem → 0.875rem */
    overline3Breakpoints: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } as const,
    /** Caption tiny: 0.65rem → 0.7rem */
    captionTiny: { xs: '0.65rem', sm: '0.7rem' } as const,
    /** Dialog/section title: 1.125rem → 1.25rem */
    dialogTitle: { xs: '1.125rem', sm: '1.25rem' } as const,
    /** Stat/display value: 1.75rem → 2.125rem */
    statValue: { xs: '1.75rem', sm: '2.125rem' } as const,
    /** Card title alt: 1.05rem → 1.35rem */
    cardTitleAlt: { xs: '1.05rem', sm: '1.35rem' } as const,
    /** Manager dashboard hero: 1.3rem → 1.8rem (md) */
    managerHero: { xs: '1.3rem', md: '1.8rem' } as const,
    /** Manager dashboard subtext: 0.75rem → 0.875rem (md) */
    managerSubtext: { xs: '0.75rem', md: '0.875rem' } as const,
    /** Tab label (e.g. manager tabs): 0.65rem → 0.75rem (md) */
    tabLabel: { xs: '0.65rem', md: '0.75rem' } as const,
    /** Body fixed at 1rem on all breakpoints */
    bodyFixedAt1: { xs: '1rem', sm: '1rem' } as const,
    /** Icon-sized text (e.g. stat labels): 1.2rem → 1.5rem */
    iconText: { xs: '1.2rem', sm: '1.5rem' } as const,
    /** Heading medium large: 1.5rem → 1.75rem */
    headingMdLg: { xs: '1.5rem', sm: '1.75rem' } as const,
    /** Body medium then smaller on sm: 0.9375rem → 0.875rem */
    bodyMediumSmall: { xs: '0.9375rem', sm: '0.875rem' } as const,
    /** Body three breakpoints: 1rem → 1.125rem → 1.25rem */
    body3Breakpoints: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } as const,
    /** Service title (4 bp): 1.125 → 1.25 → 1.25 → 1.3125 */
    serviceTitle: { xs: '1.125rem', sm: '1.25rem', md: '1.25rem', lg: '1.3125rem' } as const,
    /** Service description (4 bp): 0.9375 → 1 → 1 → 1.0625 */
    serviceDesc: { xs: '0.9375rem', sm: '1rem', md: '1rem', lg: '1.0625rem' } as const,
    /** 0.95rem → 1.125rem */
    bodyCompactToLarge: { xs: '0.95rem', sm: '1.125rem' } as const,
    /** Body 3 bp: 0.8rem → 0.875rem → 1rem */
    bodySmallToBase: { xs: '0.8rem', sm: '0.875rem', md: '1rem' } as const,
    /** Button small: 0.875 → 0.9 → 1 */
    buttonSmall: { xs: '0.875rem', sm: '0.9rem', md: '1rem' } as const,
    /** Button medium: 1 → 1.1 → 1.25 */
    buttonMedium: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } as const,
    /** Button large: 1.07 → 1.21 → 1.375 */
    buttonLarge: { xs: '1.07rem', sm: '1.21rem', md: '1.375rem' } as const,
    /** Button xlarge: 1.1375 → 1.32 → 1.5 */
    buttonXlarge: { xs: '1.1375rem', sm: '1.32rem', md: '1.5rem' } as const,
    /** Section title (option/checkbox step): 1.25rem → 1.375rem */
    optionTitle: { xs: '1.25rem', sm: '1.375rem' } as const,
    /** Heading small to medium (md): 1.1rem → 1.5rem (md) */
    headingSmMd: { xs: '1.1rem', md: '1.5rem' } as const,
    /** Metric card title: 1.5rem → 2rem (md) */
    metricTitle: { xs: '1.5rem', md: '2rem' } as const,
    /** Body small (md): 0.8rem → 0.95rem (md) */
    bodySmallMd: { xs: '0.8rem', md: '0.95rem' } as const,
    /** Subtitle (md): 1rem → 1.25rem (md) */
    subtitleMd: { xs: '1rem', md: '1.25rem' } as const,
    /** Hero stat / big number: 2rem → 2.5rem */
    heroStat: { xs: '2rem', sm: '2.5rem' } as const,
    /** Wizard hero title: 2.75rem → 3.25rem */
    wizardHero: { xs: '2.75rem', sm: '3.25rem' } as const,
    /** Top list card title: 0.9rem → 1.1rem (md) */
    topListTitle: { xs: '0.9rem', md: '1.1rem' } as const,
    /** Top list cell: 0.7rem → 0.8rem (md) */
    topListCell: { xs: '0.7rem', md: '0.8rem' } as const,
    /** Caption small: 0.65rem → 0.75rem */
    captionSmall: { xs: '0.65rem', sm: '0.75rem' } as const,
    /** Body small alt: 0.8rem → 0.85rem */
    bodySmallAlt: { xs: '0.8rem', sm: '0.85rem' } as const,
    /** Heading small large: 1.1rem → 1.5rem */
    headingSmLg: { xs: '1.1rem', sm: '1.5rem' } as const,
    /** Body subtitle (header): 0.95rem → 1.05rem */
    bodySubtitle: { xs: '0.95rem', sm: '1.05rem' } as const,
    /** Body with md dip: 1rem (xs) → 0.875rem (md) → 1rem (lg) */
    bodyMdDip: { xs: '1rem', md: '0.875rem', lg: '1rem' } as const,
    /** Section title (md): 1.1rem → 1.4rem (md) */
    sectionTitleMd: { xs: '1.1rem', md: '1.4rem' } as const,
    /** Caption extra small: 0.625rem → 0.75rem */
    captionExtraSmall: { xs: '0.625rem', sm: '0.75rem' } as const,
    /** Body card subtitle: 1.05rem → 1.2rem */
    bodyCardSubtitle: { xs: '1.05rem', sm: '1.2rem' } as const,
    /** Subtitle small: 1.15rem → 1.35rem */
    subtitleSmall: { xs: '1.15rem', sm: '1.35rem' } as const,
    /** Receipt title: 1.5rem → 1.75rem */
    receiptTitle: { xs: '1.5rem', sm: '1.75rem' } as const,
    /** MultiStep form title (3 bp): 1.125 → 1.25 → 1.5 */
    formTitle3Bp: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } as const,
    /** Careers title (3 bp): 1.25 → 1.5 → 1.75 */
    careersTitle: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } as const,
    /** Caption (md/lg only): 0.7rem → 0.75rem */
    captionMdLg: { md: '0.7rem', lg: '0.75rem' } as const,
    /** Title (md/lg only): 1.125rem → 1.25rem */
    titleMdLg: { md: '1.125rem', lg: '1.25rem' } as const,
    /** Body (md/lg only): 0.8125rem → 0.875rem */
    bodyMdLg: { md: '0.8125rem', lg: '0.875rem' } as const,
    /** Button root (theme): 0.875rem → 0.9375rem → 1rem */
    buttonRoot: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' } as const,
    /** Hero title (4 bp): 2.25rem → 3rem → 3.5rem → 4rem */
    heroTitle: { xs: '2.25rem', sm: '3rem', md: '3.5rem', lg: '4rem' } as const,
    /** Body menu to 1rem: 0.9375rem → 1rem */
    bodyMenuTo1: { xs: '0.9375rem', sm: '1rem' } as const,
} as const;

/** Responsive icon font sizes (numeric px for MUI icons). Use in sx: fontSize: responsiveIconSize.sm */
export const responsiveIconSize = {
    /** Small icon: 18 → 20 */
    sm: { xs: 18, sm: 20 } as const,
    /** Medium icon: 20 → 24 */
    md: { xs: 20, sm: 24 } as const,
    /** Medium-large icon: 24 → 28 */
    mdLg: { xs: 24, sm: 28 } as const,
    /** Avatar/card icon: 24 → 36 */
    avatarMd: { xs: 24, sm: 36 } as const,
    /** Large icon (rem): 2rem → 2.5rem */
    lg: { xs: '2rem', sm: '2.5rem' } as const,
    /** Display small (hero): 36 → 48 */
    displaySm: { xs: 36, sm: 48 } as const,
    /** Display medium (hero): 48 → 64 */
    displayMd: { xs: 48, sm: 64 } as const,
    /** Profile/avatar icon: 40 → 50 */
    profile: { xs: 40, sm: 50 } as const,
    /** Icon sm as px string: 18px → 20px */
    smPx: { xs: '18px', sm: '20px' } as const,
    /** Dashboard header icon: 22 → 28 (md) */
    dashboard: { xs: 22, md: 28 } as const,
    /** Icon 48 → 56 (e.g. Handshake) */
    icon48_56: { xs: 48, sm: 56 } as const,
    /** Icon 28 → 32 */
    icon28_32: { xs: 28, sm: 32 } as const,
    /** Icon 14 → 16 */
    icon14_16: { xs: 14, sm: 16 } as const,
    /** Icon 16 → 18 */
    icon16_18: { xs: 16, sm: 18 } as const,
    /** Icon 16 → 18 → 20 (3 bp) */
    icon16_18_20: { xs: 16, sm: 18, md: 20 } as const,
    /** Icon 32 → 48 (avatar in cards) */
    icon32_48: { xs: 32, sm: 48 } as const,
    /** Icon 18 → 24 (chat/list icons) */
    icon18_24: { xs: 18, sm: 24 } as const,
    /** Icon 32 → 40 (badge/popup) */
    icon32_40: { xs: 32, sm: 40 } as const,
    /** Icon 28 → 36 (upload icons) */
    icon28_36: { xs: 28, sm: 36 } as const,
    /** Icon 32 → 36 (force update modal) */
    icon32_36: { xs: 32, sm: 36 } as const,
    /** Icon 24 → 28 (header avatar) */
    icon24_28: { xs: 24, sm: 28 } as const,
    /** Icon 10 → 12 (tiny) */
    icon10_12: { xs: 10, sm: 12 } as const,
    /** Icon 20 → 22 */
    icon20_22: { xs: 20, sm: 22 } as const,
    /** Careers hero icon: 60 → 80 */
    careersHeroIcon: { xs: 60, sm: 80 } as const,
    /** Service card icon (4 bp): 32 → 32 → 24 → 22 px */
    serviceCardIcon: { xs: 32, sm: 32, md: 24, lg: 22 } as const,
} as const;

/** Fixed (non-responsive) font sizes for single-value use. Use in sx: fontSize: fontSizeFixed.rem0875 */
export const fontSizeFixed = {
    rem06: '0.6rem',
    rem065: '0.65rem',
    rem07: '0.7rem',
    rem075: '0.75rem',
    rem08: '0.8rem',
    rem08125: '0.8125rem',
    rem0875: '0.875rem',
    rem085: '0.85rem',
    rem09: '0.9rem',
    rem09375: '0.9375rem',
    rem095: '0.95rem',
    rem1: '1rem',
    rem13: '1.3rem',
    rem105: '1.05rem',
    rem11: '1.1rem',
    rem1125: '1.125rem',
    rem125: '1.25rem',
    rem15: '1.5rem',
    rem16: '1.6rem',
    rem4: '4rem',
} as const;

/** Fixed (non-responsive) icon sizes (numeric px). Use in sx: fontSize: iconSizeFixed.px20 */
export const iconSizeFixed = {
    px10: 10,
    px12: 12,
    px14: 14,
    px16: 16,
    px18: 18,
    px20: 20,
    px22: 22,
    px24: 24,
    px26: 26,
    px28: 28,
    px30: 30,
    px32: 32,
    px36: 36,
    px40: 40,
    px48: 48,
    px56: 56,
    px60: 60,
    px64: 64,
} as const;

// Responsive breakpoint utilities
export const breakpoints = {
    xs: 0,    // Extra small devices (phones, 0px and up)
    sm: 600,  // Small devices (tablets, 600px and up)
    md: 900,  // Medium devices (small laptops, 900px and up)
    lg: 1200, // Large devices (desktops, 1200px and up)
    xl: 1536, // Extra large devices (large desktops, 1536px and up)
};

// Responsive spacing utilities
export const responsiveSpacing = {
    mobile: {
        xs: 1,    // 8px
        sm: 2,    // 16px
        md: 3,    // 24px
        lg: 4,    // 32px
        xl: 6,    // 48px
    },
    tablet: {
        xs: 1.5,  // 12px
        sm: 2.5,  // 20px
        md: 4,    // 32px
        lg: 6,    // 48px
        xl: 8,    // 64px
    },
    desktop: {
        xs: 2,    // 16px
        sm: 3,    // 24px
        md: 5,    // 40px
        lg: 8,    // 64px
        xl: 12,   // 96px
    },
};

// Responsive typography utilities
export const responsiveTypography = {
    mobile: {
        h1: { fontSize: '2rem', lineHeight: 1.2 },
        h2: { fontSize: '1.75rem', lineHeight: 1.3 },
        h3: { fontSize: '1.5rem', lineHeight: 1.4 },
        h4: { fontSize: '1.25rem', lineHeight: 1.4 },
        h5: { fontSize: '1.125rem', lineHeight: 1.5 },
        h6: { fontSize: '1rem', lineHeight: 1.5 },
        body1: { fontSize: '0.875rem', lineHeight: 1.6 },
        body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
    },
    tablet: {
        h1: { fontSize: '2.5rem', lineHeight: 1.2 },
        h2: { fontSize: '2rem', lineHeight: 1.3 },
        h3: { fontSize: '1.75rem', lineHeight: 1.4 },
        h4: { fontSize: '1.5rem', lineHeight: 1.4 },
        h5: { fontSize: '1.25rem', lineHeight: 1.5 },
        h6: { fontSize: '1.125rem', lineHeight: 1.5 },
        body1: { fontSize: '1rem', lineHeight: 1.6 },
        body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    },
    desktop: {
        h1: { fontSize: '3rem', lineHeight: 1.2 },
        h2: { fontSize: '2.5rem', lineHeight: 1.3 },
        h3: { fontSize: '2rem', lineHeight: 1.4 },
        h4: { fontSize: '1.75rem', lineHeight: 1.4 },
        h5: { fontSize: '1.5rem', lineHeight: 1.5 },
        h6: { fontSize: '1.25rem', lineHeight: 1.5 },
        body1: { fontSize: '1.125rem', lineHeight: 1.6 },
        body2: { fontSize: '1rem', lineHeight: 1.6 },
    },
};

// Responsive container utilities
export const responsiveContainer = {
    mobile: {
        maxWidth: '100%',
        padding: '0 16px',
    },
    tablet: {
        maxWidth: '100%',
        padding: '0 24px',
    },
    desktop: {
        maxWidth: '1200px',
        padding: '0 32px',
    },
};

// Responsive grid utilities
export const responsiveGrid = {
    mobile: {
        columns: 1,
        gap: 16,
    },
    tablet: {
        columns: 2,
        gap: 24,
    },
    desktop: {
        columns: 3,
        gap: 32,
    },
};

// Responsive button utilities
export const responsiveButton = {
    mobile: {
        padding: '8px 16px',
        fontSize: '0.875rem',
        minHeight: 40,
    },
    tablet: {
        padding: '10px 20px',
        fontSize: '0.9375rem',
        minHeight: 44,
    },
    desktop: {
        padding: '12px 24px',
        fontSize: '1rem',
        minHeight: 48,
    },
};

// Responsive card utilities
export const responsiveCard = {
    mobile: {
        padding: 16,
        borderRadius: 8,
        marginBottom: 16,
    },
    tablet: {
        padding: 24,
        borderRadius: 12,
        marginBottom: 24,
    },
    desktop: {
        padding: 32,
        borderRadius: 16,
        marginBottom: 32,
    },
};

// Helper function to get responsive value based on breakpoint
export const getResponsiveValue = (
    mobileValue: any,
    tabletValue?: any,
    desktopValue?: any
) => {
    return {
        xs: mobileValue,
        sm: tabletValue || mobileValue,
        md: desktopValue || tabletValue || mobileValue,
    };
};

// Helper function to get responsive spacing
export const getResponsiveSpacing = (size: keyof typeof responsiveSpacing.mobile) => {
    return {
        xs: responsiveSpacing.mobile[size],
        sm: responsiveSpacing.tablet[size],
        md: responsiveSpacing.desktop[size],
    };
};

// Helper function to get responsive typography
export const getResponsiveTypography = (variant: keyof typeof responsiveTypography.mobile) => {
    return {
        xs: responsiveTypography.mobile[variant],
        sm: responsiveTypography.tablet[variant],
        md: responsiveTypography.desktop[variant],
    };
};

// Helper function to get responsive container styles
export const getResponsiveContainerStyles = () => {
    return {
        xs: responsiveContainer.mobile,
        sm: responsiveContainer.tablet,
        md: responsiveContainer.desktop,
    };
};

// Helper function to get responsive grid styles
export const getResponsiveGridStyles = () => {
    return {
        xs: responsiveGrid.mobile,
        sm: responsiveGrid.tablet,
        md: responsiveGrid.desktop,
    };
};

// Helper function to get responsive button styles
export const getResponsiveButtonStyles = () => {
    return {
        xs: responsiveButton.mobile,
        sm: responsiveButton.tablet,
        md: responsiveButton.desktop,
    };
};

// Helper function to get responsive card styles
export const getResponsiveCardStyles = () => {
    return {
        xs: responsiveCard.mobile,
        sm: responsiveCard.tablet,
        md: responsiveCard.desktop,
    };
};

// Media query helpers
export const mediaQueries = {
    mobile: '@media (max-width: 599px)',
    tablet: '@media (min-width: 600px) and (max-width: 899px)',
    desktop: '@media (min-width: 900px)',
    mobileAndTablet: '@media (max-width: 899px)',
    tabletAndDesktop: '@media (min-width: 600px)',
};

// Responsive hook for common breakpoints
export const useResponsive = () => {
    return {
        isMobile: (theme: Theme) => theme.breakpoints.down('sm'),
        isTablet: (theme: Theme) => theme.breakpoints.between('sm', 'md'),
        isDesktop: (theme: Theme) => theme.breakpoints.up('md'),
        isMobileOrTablet: (theme: Theme) => theme.breakpoints.down('md'),
        isTabletOrDesktop: (theme: Theme) => theme.breakpoints.up('sm'),
    };
};

// Responsive layout helpers
export const responsiveLayout = {
    // Header styles
    header: {
        mobile: {
            height: 56,
            padding: '0 16px',
            fontSize: '1rem',
        },
        tablet: {
            height: 64,
            padding: '0 24px',
            fontSize: '1.125rem',
        },
        desktop: {
            height: 64,
            padding: '0 32px',
            fontSize: '1.25rem',
        },
    },
    // Sidebar styles
    sidebar: {
        mobile: {
            width: '85%',
            maxWidth: 320,
        },
        tablet: {
            width: 320,
        },
        desktop: {
            width: 360,
        },
    },
    // Main content styles
    mainContent: {
        mobile: {
            padding: '16px',
        },
        tablet: {
            padding: '24px',
        },
        desktop: {
            padding: '32px',
        },
    },
    // Footer styles
    footer: {
        mobile: {
            padding: '32px 16px',
            fontSize: '0.875rem',
        },
        tablet: {
            padding: '48px 24px',
            fontSize: '1rem',
        },
        desktop: {
            padding: '64px 32px',
            fontSize: '1.125rem',
        },
    },
};

// Export all utilities
export default {
    responsiveFontSize,
    responsiveIconSize,
    fontSizeFixed,
    iconSizeFixed,
    breakpoints,
    responsiveSpacing,
    responsiveTypography,
    responsiveContainer,
    responsiveGrid,
    responsiveButton,
    responsiveCard,
    responsiveLayout,
    mediaQueries,
    getResponsiveValue,
    getResponsiveSpacing,
    getResponsiveTypography,
    getResponsiveContainerStyles,
    getResponsiveGridStyles,
    getResponsiveButtonStyles,
    getResponsiveCardStyles,
    useResponsive,
}; 